import { useMemo } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { store, useStore } from "@/lib/store";
import { ConnectPlatformCard } from "@/views/shared";
import { MachinePresence, type MachinePresenceEntry } from "./parts/MachinePresence";
import { RecentSessions, type RecentSessionEntry } from "./parts/RecentSessions";
import { AssistantShortcuts, ServerShortcuts } from "./parts/PlatformShortcuts";
import { RecentlyShipped, projectDeployedAt } from "./parts/RecentlyShipped";

const RECENT_SESSION_LIMIT = 5;
const SHIPPED_LIMIT = 5;
const SHORTCUT_LIMIT = 3;

function sessionRecency(entry: RecentSessionEntry): number {
  return entry.session.lastMessageAt ?? entry.session.updatedAt;
}

export interface HomeViewProps {
  // The New Session dialog lives in the shell (same one the sidebar opens);
  // the CTA only renders when the shell wires it in.
  onNewSession?: () => void;
}

export function HomeView({ onNewSession }: HomeViewProps) {
  const state = useStore();

  const recentSessions = useMemo(() => {
    const entries: RecentSessionEntry[] = [];
    if (!state.hostedMode) {
      for (const sessions of Object.values(state.sessionsByProject)) {
        for (const session of sessions) {
          entries.push({ session, source: "local", machineLabel: "This machine" });
        }
      }
    }
    for (const [machineId, remote] of Object.entries(state.remote)) {
      // The local box also registers with the hub; its sessions are already
      // listed under "This machine", so skip the duplicate remote copy.
      if (machineId === state.localMachineId) continue;
      for (const sessions of Object.values(remote.sessionsByProject)) {
        for (const session of sessions) {
          entries.push({ session, source: machineId, machineLabel: remote.machine.hostname });
        }
      }
    }
    return entries
      .sort((a, b) => sessionRecency(b) - sessionRecency(a))
      .slice(0, RECENT_SESSION_LIMIT);
  }, [state.sessionsByProject, state.remote, state.hostedMode, state.localMachineId]);

  // Hosted mode has no local project fetch, so its "loaded" signal is the
  // machine list; locally it is the projects fetch.
  const sessionsLoaded = state.hostedMode ? state.remoteMachinesLoaded : state.projectsLoaded;

  const machines = useMemo(() => {
    const list: MachinePresenceEntry[] = [];
    if (!state.hostedMode) {
      const liveCount = Object.values(state.sessionsByProject)
        .flat()
        .filter((s) => s.status === "live").length;
      list.push({
        key: "local",
        label: "This machine",
        online: state.connected,
        activeSessionCount: liveCount,
      });
    }
    const remote = Object.values(state.remote)
      .filter((r) => r.machine.machineId !== state.localMachineId)
      .sort((a, b) => {
        if (a.machine.online !== b.machine.online) return a.machine.online ? -1 : 1;
        return a.machine.hostname.localeCompare(b.machine.hostname);
      });
    for (const r of remote) {
      list.push({
        key: r.machine.machineId,
        label: r.machine.hostname,
        online: r.machine.online,
        activeSessionCount: r.machine.activeSessionCount,
      });
    }
    return list;
  }, [state.hostedMode, state.sessionsByProject, state.connected, state.remote, state.localMachineId]);

  const shipped = useMemo(() => {
    return [...state.platform.projects.items]
      .sort((a, b) => {
        const ta = projectDeployedAt(a);
        const tb = projectDeployedAt(b);
        return (tb ? new Date(tb).getTime() : 0) - (ta ? new Date(ta).getTime() : 0);
      })
      .slice(0, SHIPPED_LIMIT);
  }, [state.platform.projects.items]);

  const { platform } = state;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-2xl px-4 py-6">
        <header className="mb-6">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            CodeZero
          </p>
          <div className="mt-1 flex items-center justify-between gap-3">
            <h1 className="text-xl font-semibold text-foreground">Welcome back</h1>
            {onNewSession && (
              <Button variant="hero" size="sm" onClick={onNewSession}>
                <Plus className="mr-1.5 h-4 w-4" />
                New session
              </Button>
            )}
          </div>
        </header>

        <RecentSessions
          entries={recentSessions}
          loaded={sessionsLoaded}
          onOpen={(entry) =>
            void store.openSession(entry.session.projectSlug, entry.session.id, entry.source)
          }
        />

        <MachinePresence
          machines={machines}
          onOpen={() => store.navigate({ name: "machines" })}
        />

        {platform.available ? (
          <>
            <RecentlyShipped
              projects={shipped}
              loaded={platform.projects.loaded}
              error={platform.projects.error}
              onOpen={(id) => store.navigate({ name: "project", id })}
              onViewAll={() => store.navigate({ name: "projects" })}
            />
            <AssistantShortcuts
              assistants={platform.assistants.items.slice(0, SHORTCUT_LIMIT)}
              loaded={platform.assistants.loaded}
              error={platform.assistants.error}
              onOpen={(id) => store.navigate({ name: "assistant", id })}
              onViewAll={() => store.navigate({ name: "assistants" })}
            />
            <ServerShortcuts
              servers={platform.mcpServers.items.slice(0, SHORTCUT_LIMIT)}
              loaded={platform.mcpServers.loaded}
              error={platform.mcpServers.error}
              onOpen={(id) => store.navigate({ name: "server", id })}
              onViewAll={() => store.navigate({ name: "servers" })}
            />
          </>
        ) : (
          <ConnectPlatformCard />
        )}
      </div>
    </div>
  );
}
